import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';

import useToast from '@yaydoo/react-components/lib/hooks/useToast';

import PayButton from '@components/PayButton';
import GenericCardFields from '@containers/checkout/payment-form/CardPaymentForm/GenericCardFields';
import { PartnerFormProps } from '@containers/checkout/payment-form/types';
import useStore from '@hooks/useStore';
import { FormPayload } from '@store/blumon';
import { Partner } from '@constants/paymentLink';
import FormPartnerLogo from '@components/FormPartnerLogo';

const BlumonCardForm: FC<PartnerFormProps> = ({ onValidationError }) => {
  const { handleSubmit } = useFormContext();
  const { t } = useTranslation();
  const { openToast } = useToast();
  const { blumon, links, checkout } = useStore();

  const handleError = (error: { message: string }) => {
    const { message = '' } = error;
    openToast({
      type: 'error',
      title: t('checkoutScreen.payment.errors.card.title'),
      message: message || t('checkoutScreen.payment.errors.card.unknown'),
    });
  };

  const onSubmit = async (params: FormPayload) => {
    try {
      const result = await blumon.pay({
        ...params,
        paymentLinkId: links.paymentLink.paymentLinkId,
      });
      if (result.success) {
        links.setAsPaid();
      } else {
        handleError({ message: '' });
      }
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  return (
    <>
      <GenericCardFields />
      <PayButton
        onClick={handleSubmit(onSubmit as SubmitHandler<FieldValues>, onValidationError)}
        disabled={blumon.isProcessing || !checkout.isAllowedToPayWithCard}
        loading={blumon.isProcessing}
        label={t('checkoutScreen.payment.methods.submit')}
      />
      <FormPartnerLogo partner={Partner.BLUMON} />
    </>
  );
};

export default observer(BlumonCardForm);
